import type {
  QuestionData,
  QuestionOption,
  QuestionOptionTranslationData,
  QuestionTranslationData,
} from 'src/types/formQuestionnaire';

export const DEFAULT_FORM_EDITING_LOCALE = 'en-GB';

function findQuestionTranslationRow(
  translations: QuestionTranslationData[] | undefined,
  languageCode: string,
): QuestionTranslationData | undefined {
  return translations?.find((t) => t.languageCode === languageCode);
}

function findOptionTranslationRow(
  translations: QuestionOptionTranslationData[] | undefined,
  languageCode: string,
): QuestionOptionTranslationData | undefined {
  return translations?.find((t) => t.languageCode === languageCode);
}

function pruneFormTranslationUiRow(
  row: QuestionTranslationData,
): QuestionTranslationData {
  const next = { ...row };
  if (next.uiPublishQuestionTextError == null) {
    delete next.uiPublishQuestionTextError;
  }
  if (next.uiPublishDescriptionError == null) {
    delete next.uiPublishDescriptionError;
  }
  if (next.uiPublishOptionsError == null) {
    delete next.uiPublishOptionsError;
  }
  return next;
}

function upsertQuestionTranslationRows(
  existing: QuestionTranslationData[] | undefined,
  languageCode: string,
  patch: Partial<
    Pick<
      QuestionTranslationData,
      | 'questionText'
      | 'description'
      | 'uiPublishQuestionTextError'
      | 'uiPublishDescriptionError'
      | 'uiPublishOptionsError'
    >
  >,
): QuestionTranslationData[] {
  const list = [...(existing ?? [])];
  const idx = list.findIndex((t) => t.languageCode === languageCode);
  const prev = idx >= 0 ? list[idx] : undefined;
  const nextRow: QuestionTranslationData = {
    languageCode,
    questionText:
      patch.questionText !== undefined
        ? patch.questionText
        : (prev?.questionText ?? ''),
    description:
      patch.description !== undefined
        ? (patch.description ?? null)
        : (prev?.description ?? null),
    uiPublishQuestionTextError: Object.hasOwn(
      patch,
      'uiPublishQuestionTextError',
    )
      ? (patch.uiPublishQuestionTextError ?? null)
      : prev?.uiPublishQuestionTextError,
    uiPublishDescriptionError: Object.hasOwn(patch, 'uiPublishDescriptionError')
      ? (patch.uiPublishDescriptionError ?? null)
      : prev?.uiPublishDescriptionError,
    uiPublishOptionsError: Object.hasOwn(patch, 'uiPublishOptionsError')
      ? (patch.uiPublishOptionsError ?? null)
      : prev?.uiPublishOptionsError,
  };
  if (idx >= 0) {
    list[idx] = pruneFormTranslationUiRow(nextRow);
  } else {
    list.push(pruneFormTranslationUiRow(nextRow));
  }
  return list;
}

function upsertOptionTranslationRows(
  existing: QuestionOptionTranslationData[] | undefined,
  languageCode: string,
  optionText: string,
): QuestionOptionTranslationData[] {
  const list = [...(existing ?? [])];
  const idx = list.findIndex((t) => t.languageCode === languageCode);
  const prev = idx >= 0 ? list[idx] : undefined;
  const merged: QuestionOptionTranslationData = {
    ...(prev ?? { languageCode, optionText: '' }),
    languageCode,
    optionText,
  };
  delete merged.uiPublishOptionTextError;
  if (idx >= 0) {
    list[idx] = merged;
  } else {
    list.push(merged);
  }
  return list;
}

export function denormalizeQuestionForEditingLocale(
  question: QuestionData,
  editingLocale: string,
): QuestionData {
  if (editingLocale === DEFAULT_FORM_EDITING_LOCALE) {
    return question;
  }
  const tr = findQuestionTranslationRow(question.translations, editingLocale);
  const localizedOptions = question.options?.map((opt) => {
    const optTr = findOptionTranslationRow(opt.translations, editingLocale);
    return {
      ...opt,
      text: optTr?.optionText ?? '',
      textError:
        editingLocale !== DEFAULT_FORM_EDITING_LOCALE
          ? (optTr?.uiPublishOptionTextError ?? null)
          : opt.textError,
    };
  });
  return {
    ...question,
    questionText: tr?.questionText ?? '',
    description: tr?.description ?? '',
    ...(localizedOptions && { options: localizedOptions }),
  };
}

export function mergePartialQuestionDataForLocale(
  storageQuestion: QuestionData,
  editingLocale: string,
  patch: Partial<QuestionData>,
): Partial<QuestionData> {
  if (editingLocale === DEFAULT_FORM_EDITING_LOCALE) {
    return patch;
  }

  const { questionText, description, options: patchOptions, ...rest } = patch;
  const out: Partial<QuestionData> = { ...rest };

  if (questionText !== undefined || description !== undefined) {
    const tPatch: Partial<
      Pick<QuestionTranslationData, 'questionText' | 'description'>
    > = {};
    if (questionText !== undefined) {
      tPatch.questionText = questionText;
    }
    if (description !== undefined) {
      tPatch.description = description ?? null;
    }
    const clearUi: Partial<
      Pick<
        QuestionTranslationData,
        | 'uiPublishQuestionTextError'
        | 'uiPublishDescriptionError'
        | 'uiPublishOptionsError'
      >
    > = {};
    if (questionText !== undefined) {
      clearUi.uiPublishQuestionTextError = undefined;
      clearUi.uiPublishOptionsError = undefined;
    }
    if (description !== undefined) {
      clearUi.uiPublishDescriptionError = undefined;
    }
    out.translations = upsertQuestionTranslationRows(
      storageQuestion.translations,
      editingLocale,
      { ...tPatch, ...clearUi },
    );
  }

  if (patchOptions !== undefined) {
    out.options = mergeOptionsForEditingLocale(
      storageQuestion,
      editingLocale,
      patchOptions,
    );
  } else if ('options' in patch && patch.options === undefined) {
    out.options = undefined;
  }

  return out;
}

export function mergeOptionsForEditingLocale(
  storageQuestion: QuestionData,
  editingLocale: string,
  patchedOptions: QuestionOption[],
): QuestionOption[] {
  if (editingLocale === DEFAULT_FORM_EDITING_LOCALE) {
    return patchedOptions;
  }
  return patchedOptions.map((pOpt) => {
    const baseOpt =
      storageQuestion.options?.find((o) => o.id === pOpt.id) ?? pOpt;
    return {
      ...baseOpt,
      ...pOpt,
      text: baseOpt.text,
      translations: upsertOptionTranslationRows(
        baseOpt.translations,
        editingLocale,
        pOpt.text,
      ),
    };
  });
}

export function resolveDefaultEditingLocale(
  languages: { value: string; isDefault?: boolean }[],
): string {
  const fromDefault = languages.find((l) => l.isDefault)?.value;
  if (fromDefault) return fromDefault;
  const en = languages.find((l) => l.value === DEFAULT_FORM_EDITING_LOCALE);
  if (en) return en.value;
  return languages[0]?.value ?? DEFAULT_FORM_EDITING_LOCALE;
}
